import { useState } from "react";
import { Send, Phone, MessageSquare, CheckCircle, XCircle, Loader } from "lucide-react";
import { toast } from "react-toastify";
import api from "../../lib/axios";
import { RoleBasedLayout } from "../../components/RoleBasedLayout";

const TestSMSPage = () => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [message, setMessage] = useState("This is a test SMS from StorePilot. If you receive this, SMS functionality is working correctly!");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!phoneNumber.trim()) {
      toast.error("Please enter a phone number");
      return;
    }

    if (!message.trim()) {
      toast.error("Please enter a message");
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await api.post("/test/sms", {
        phoneNumber: phoneNumber.trim(),
        message: message.trim(),
      });

      if (response.data.success) {
        setResult({ success: true, message: response.data.message });
        toast.success("SMS sent successfully!");
        setPhoneNumber("");
        setMessage("This is a test SMS from StorePilot. If you receive this, SMS functionality is working correctly!");
      } else {
        setResult({ success: false, message: response.data.message });
        toast.error(response.data.message || "Failed to send SMS");
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "Failed to send SMS. Please check your Twilio configuration.";
      setResult({ success: false, message: errorMessage });
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <RoleBasedLayout>
      <div className="flex flex-col w-full h-full p-4 md:p-6 lg:p-8">
        <div className="max-w-2xl mx-auto w-full">
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              Test SMS Functionality
            </h1>
            <p className="text-gray-600">
              Send a test SMS message to verify that Twilio is configured correctly.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Phone Number Input */}
              <div>
                <label
                  htmlFor="phoneNumber"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Phone Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Phone className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="tel"
                    id="phoneNumber"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+1234567890"
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none transition"
                    disabled={loading}
                    required
                  />
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  Format: Include country code (e.g., +1234567890)
                </p>
              </div>

              {/* Message Input */}
              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Message
                </label>
                <div className="relative">
                  <div className="absolute top-3 left-3 pointer-events-none">
                    <MessageSquare className="h-5 w-5 text-gray-400" />
                  </div>
                  <textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={6}
                    placeholder="Enter your test message..."
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none transition resize-none"
                    disabled={loading}
                    required
                  />
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  {message.length} characters
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || !phoneNumber.trim() || !message.trim()}
                className="w-full flex items-center justify-center gap-2 bg-primary text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {loading ? (
                  <>
                    <Loader className="h-5 w-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    Send Test SMS
                  </>
                )}
              </button>
            </form>

            {/* Result Display */}
            {result && (
              <div
                className={`mt-6 p-4 rounded-lg border-2 ${
                  result.success
                    ? "bg-green-50 border-green-200"
                    : "bg-red-50 border-red-200"
                }`}
              >
                <div className="flex items-start gap-3">
                  {result.success ? (
                    <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <h3
                      className={`font-medium mb-1 ${
                        result.success ? "text-green-800" : "text-red-800"
                      }`}
                    >
                      {result.success ? "Success" : "Error"}
                    </h3>
                    <p
                      className={`text-sm ${
                        result.success ? "text-green-700" : "text-red-700"
                      }`}
                    >
                      {result.message}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Configuration Info */}
            <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-medium text-blue-900 mb-2">
                Configuration Requirements
              </h3>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li>Twilio Account SID must be set in <code className="bg-blue-100 px-1 rounded">TWILIO_ACCOUNT_SID</code></li>
                <li>Twilio Auth Token must be set in <code className="bg-blue-100 px-1 rounded">TWILIO_AUTH_TOKEN</code></li>
                <li>Twilio Phone Number must be set in <code className="bg-blue-100 px-1 rounded">TWILIO_PHONE_NUMBER</code></li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </RoleBasedLayout>
  );
};

export default TestSMSPage;

